import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/authContext";
import { SidebarLink } from "./sidebarLink";
import { openForethoughtWidget } from "../../hooks/useForethoughtWidget";
import {
  CircleHelp,
  ChevronLeft,
  ChevronRight,
  Users,
  Bug,
} from "lucide-react";
import { toggleOAuthDebug } from "../../lib/oauthDebugBus";
import { cn } from "../../lib/utils.ts";
import { hasAnyPermission } from "../../lib/permissions.ts";
import { Badge } from "../ui/badge.tsx";
import { ExtractionSidebar } from "./extractionSidebar.tsx";
import { AuditSidebar } from "./AuditSidebar.tsx";
import { AdminSidebar } from "./AdminSidebar.tsx";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";

// Session-detail routes auto-collapse the sidebar to give the workpaper room.
// The captured group is the session id, so the tabs of one session
// (overview / documents / workpaper-view / workpaper-v2) all share a key and
// moving between them is not a new entry.
const SESSION_ROUTE = /\/audit\/(?:control-)?sessions\/session\/([^/?#]+)/;

const getSessionKey = (pathname: string): string | null =>
  SESSION_ROUTE.exec(pathname)?.[1] ?? null;

export const SidebarContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, workspace, workspaceMember, logout: logoutAuth } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  // The Background Tasks popover content renders in a portal, outside this
  // div's DOM subtree, and sits a `sideOffset` gap away from the sidebar
  // edge. Moving the pointer from the trigger into the popover crosses that
  // gap and fires onMouseLeave here, which would otherwise collapse the
  // sidebar out from under an open popover. Keeping the sidebar expanded for
  // as long as the popover reports itself open sidesteps the gap entirely,
  // rather than patching mouse events.
  const [isBackgroundTasksOpen, setIsBackgroundTasksOpen] = useState(false);
  // Same reason for the footer's account menu.
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  const sessionKey = getSessionKey(location.pathname);

  // Single source of truth for the pin state, tagged with the session it was
  // derived for. Entering a session — or switching to a different one — pins
  // the sidebar closed exactly once; after that the value is whatever the user
  // last chose with the chevron, so a manual expand survives a move to a
  // sibling tab of the same session.
  //
  // Deliberately ONE piece of state: the previous shapes of this component kept
  // an `isPinned`/`prevIsPinned` pair that had to be hand-synchronised across
  // the auto-collapse, the restore, and the chevron handler, and each shape
  // shipped a bug. Auto-collapse is now a function of the route, not a replayed
  // sequence of writes.
  const [pin, setPin] = useState<{
    sessionKey: string | null;
    isPinned: boolean;
  }>(() => ({ sessionKey, isPinned: sessionKey === null }));

  // Adjusting state during render when the route it derives from changes — the
  // documented React pattern; it re-renders once and settles, no effect needed.
  if (pin.sessionKey !== sessionKey) {
    setPin({ sessionKey, isPinned: sessionKey === null });
  }
  const isPinned = pin.isPinned;

  const logout = () => {
    logoutAuth();
    navigate("/signin");
  };

  // React to the actual route (location.pathname), not the navigate identity.
  useEffect(() => {
    const path = location.pathname;
    if (path === "/dashboard/" || path === "/dashboard") {
      navigate(
        workspace?.auditEnabled ? "/audit" : "/dashboard/extraction/new",
      );
      return;
    }
  }, [location.pathname, navigate, workspace?.auditEnabled]);

  const isCollapsed = !(
    isPinned ||
    isHovered ||
    isBackgroundTasksOpen ||
    isAccountMenuOpen
  );

  return (
    <div className="flex h-screen">
      <div
        data-testid="app-sidebar"
        className={cn(
          "flex flex-col h-screen border-r border-stone-200 border-solid",
          "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          "[will-change:width]",
          isCollapsed ? "w-16" : "w-[200px]",
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="p-2">
          <div className="flex items-center justify-between px-3 py-1">
            <div
              className={cn(
                "relative flex items-center h-8 overflow-hidden transition-all duration-300",
                isCollapsed ? "w-8" : "w-32",
              )}
            >
              <img
                src="/logo_orange_bg.svg"
                alt="Midship"
                className={cn(
                  "h-8 w-8 flex-shrink-0 transition-opacity duration-300",
                  isCollapsed ? "opacity-100" : "opacity-0",
                )}
              />
              <img
                src="/midship_optro_lockup.svg"
                alt="Midship by Optro"
                className={cn(
                  "absolute left-0 h-8 w-auto max-w-none transition-opacity duration-300 dark:hidden",
                  isCollapsed ? "opacity-0" : "opacity-100",
                )}
              />
              <img
                src="/midship_optro_lockup_dark.svg"
                alt="Midship by Optro"
                className={cn(
                  "absolute left-0 h-8 w-auto max-w-none transition-opacity duration-300 hidden dark:block",
                  isCollapsed ? "opacity-0" : "opacity-100",
                )}
              />
            </div>
            <button
              onClick={() => {
                const next = !isPinned;
                setPin({ sessionKey, isPinned: next });
                if (!next) setIsHovered(false);
              }}
              className={cn(
                "text-gray-500 hover:text-gray-700 transition-all duration-300 flex-shrink-0",
                isCollapsed
                  ? "opacity-0 w-0 h-0 overflow-hidden pointer-events-none"
                  : "opacity-100",
              )}
            >
              {isPinned ? (
                <ChevronLeft size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>
          </div>
          {workspace?.name ? (
            <div
              className={cn(
                "px-3 overflow-hidden transition-all duration-300",
                isCollapsed ? "h-0 opacity-0" : "h-auto opacity-100 mt-1",
              )}
            >
              <Badge
                data-testid="app-workspace-name-badge"
                variant="secondary"
                className="max-w-full font-normal text-stone-600"
              >
                <span className="truncate">{workspace.name}</span>
              </Badge>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto min-h-0">
          {workspace?.auditEnabled ? (
            <AuditSidebar
              isCollapsed={isCollapsed}
              onBackgroundTasksOpenChange={setIsBackgroundTasksOpen}
            />
          ) : (
            <ExtractionSidebar isCollapsed={isCollapsed} />
          )}
          {hasAnyPermission(workspaceMember, [
            "workspace_management",
            "manage_approvals",
          ]) ? (
            <AdminSidebar isCollapsed={isCollapsed} />
          ) : (
            <SidebarLink
              path="/admin/workspace_members"
              title="Users"
              icon={<Users size={20} />}
              isCollapsed={isCollapsed}
            />
          )}

          <SidebarLink
            onClick={openForethoughtWidget}
            title="Support"
            icon={<CircleHelp size={20} />}
            isCollapsed={isCollapsed}
          />

          {/* DEV-only: toggle the Optro OAuth diagnostics overlay (off by
              default). `import.meta.env.DEV` is statically false in every
              `vite build`, so this link tree-shakes out of production. */}
          {import.meta.env.DEV && (
            <SidebarLink
              onClick={toggleOAuthDebug}
              title="Optro Debug"
              icon={<Bug size={20} />}
              isCollapsed={isCollapsed}
            />
          )}
        </div>

        <div className="mt-auto p-2">
          <WorkspaceSwitcher isCollapsed={isCollapsed} />
          <ThemeToggle
            isCollapsed={isCollapsed}
            email={user?.email}
            onLogout={logout}
            onOpenChange={setIsAccountMenuOpen}
          />
        </div>
      </div>
      <div
        className="flex-1 min-w-0 overflow-hidden"
        data-testid="app-main-content"
      >
        <Outlet />
      </div>
    </div>
  );
};
